import type { SourceProviderIcon, UserTaskSource } from "@/types/userTasks.ts";
import { OKTHX_CONVERSATION } from "./taskRowCopy.ts";

/**
 * The page a source opens, named rather than addressed. Web and native route to
 * the same two places under different URLs, so which page a source opens is
 * decided here and each platform writes its own link.
 */
export type SourceTarget =
  | { kind: "thread"; threadId: string }
  | { kind: "source"; sourceId: string };

/**
 * A source's own page: a mail or a document, or a conversation with okthx. A
 * text opens okthx's thread for its conversation, where every message is
 * (BIG-8841), and its own page only when okthx has no thread for it.
 */
export function sourceTarget(source: UserTaskSource): SourceTarget | undefined {
  if (source.threadId) return { kind: "thread", threadId: String(source.threadId) };
  const id = source.urn.split(":").at(-1);
  if (!id) return undefined;
  if (source.type === "source") return { kind: "source", sourceId: id };
  if (source.type === "thread") return { kind: "thread", threadId: id };
  return undefined;
}

export type SourceRef = {
  key: string;
  label: string;
  target: SourceTarget | undefined;
  /**
   * The service this source came through, so a row can wear its mark.
   *
   * The first source of a deduplicated group decides it, which is the oldest —
   * the same one whose title the row shows. A source the server could not
   * attribute carries none, and the row stands without a mark rather than
   * borrowing one from a sibling that is a different service.
   */
  providerIcon: SourceProviderIcon | undefined;
  /**
   * The source is a conversation with okthx, which wears okthx's own mark.
   *
   * Read from the wire type rather than from `target.kind`: a text's target is
   * a thread as well — okthx's thread for it — and a text came through a
   * service that has a mark of its own.
   */
  conversation: boolean;
};

/** What two sources share when they open the same page. */
const targetKey = (target: SourceTarget): string =>
  target.kind === "thread" ? `thread:${target.threadId}` : `source:${target.sourceId}`;

/**
 * The links a task's sources show, oldest first, one for each page they open:
 * texts from one conversation are one link to it, as is the conversation when
 * the task also cites it. Each text is only a line of the conversation.
 *
 * A title is shown once as well. Mail in one exchange shares a subject, so a
 * task found across three replies would list "Re: Renewal pricing" three times
 * — three real pages, and nothing on screen to tell them apart or to suggest
 * which to open. The first one wins, which is the oldest, and the rest of the
 * evidence stays where the whole of it already lives: the task's own page.
 *
 * Only a real title, though. "Source" is what a source without one shows, and
 * two of those are still two different places — a task both asked about and
 * handed off carries two fresh okthx threads. Deduplicating on the placeholder
 * would drop the second and leave its page unreachable from the row.
 */
export function sourceRefs(
  sources: UserTaskSource[],
  workedInThreadId?: number | null,
): SourceRef[] {
  const refs = new Map<string, SourceRef>();
  const titled = new Set<string>();
  for (const source of sources) {
    const target = sourceTarget(source);
    const key = target ? targetKey(target) : source.urn;
    if (refs.has(key)) continue;
    const title = source.title;
    if (title) {
      if (titled.has(title)) continue;
      titled.add(title);
    }
    refs.set(key, {
      key,
      label: title || "Source",
      target,
      providerIcon: source.providerIcon ?? undefined,
      conversation: source.type === "thread",
    });
  }
  // The thread okthx works the task in, last. It is not evidence the task came
  // from — which is what every other row here is — but it is a page of ours the
  // task names, and the only one the card used to open on its own. Skipped when
  // a source already opens it: a task okthx was asked about cites that thread.
  if (workedInThreadId) {
    const target: SourceTarget = { kind: "thread", threadId: String(workedInThreadId) };
    const key = targetKey(target);
    if (!refs.has(key)) {
      refs.set(key, {
        key,
        label: OKTHX_CONVERSATION,
        target,
        providerIcon: undefined,
        conversation: true,
      });
    }
  }
  return [...refs.values()];
}
